import { useState, useEffect } from 'react';
import { api } from '../services/api';

/**
 * Consolidations page — lists active consolidated shipments grouped by dealer.
 * From here a shipper can: set freight quote, book the shipment, mark shipped,
 * cancel. Adding/removing orders happens from the per-order Shipping tab in
 * factory admin.
 */

interface ConsolidatedShipment {
  id: string;
  dealerId: string;
  dealerCompany: string;
  status: string;
  freightTotalAmount: number | null;
  selectedCarrierName: string | null;
  bolNumber: string | null;
  proNumber: string | null;
  trackingNumber: string | null;
  bookedAt: string | null;
  shippedAt: string | null;
  orderCount: number;
  totalItemCount: number;
  createdAt: string;
}

const STATUSES = ['building', 'quoted', 'booked', 'shipped', 'delivered', 'cancelled'] as const;

const STATUS_COLORS: Record<string, string> = {
  building: 'bg-amber-100 text-amber-800 border-amber-300',
  quoted: 'bg-blue-100 text-blue-800 border-blue-300',
  booked: 'bg-purple-100 text-purple-800 border-purple-300',
  shipped: 'bg-green-100 text-green-800 border-green-300',
  delivered: 'bg-gray-200 text-gray-700 border-gray-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};

function fmt(n: number | null | undefined) {
  if (n == null) return '—';
  return '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function ConsolidationsPage() {
  const [shipments, setShipments] = useState<ConsolidatedShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('active'); // 'active' or specific status
  const [selected, setSelected] = useState<any | null>(null);
  const [action, setAction] = useState<{ kind: 'quote' | 'book' | 'ship' | 'edit' | null; data: any }>({ kind: null, data: {} });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter === 'active' ? {} : { status: filter };
      const data = await api.consolidations.list(params);
      const filtered = filter === 'active'
        ? data.filter((s: ConsolidatedShipment) => !['delivered', 'cancelled'].includes(s.status))
        : data;
      setShipments(filtered);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id: string) => {
    try {
      const detail = await api.consolidations.get(id);
      setSelected(detail);
    } catch (err: any) { setError(err.message); }
  };

  const submitQuote = async () => {
    if (!selected) return;
    const total = parseFloat(action.data.freightTotalAmount);
    if (!total || isNaN(total) || total <= 0) {
      alert('Enter a positive freight total');
      return;
    }
    const actualCost = action.data.freightActualCost !== '' && action.data.freightActualCost != null
      ? parseFloat(action.data.freightActualCost)
      : null;
    if (actualCost != null && (isNaN(actualCost) || actualCost < 0)) {
      alert('Our carrier cost must be a positive number (or left blank)');
      return;
    }
    try {
      const updated = await api.consolidations.setQuote(selected.id, {
        freightTotalAmount: total,
        freightBaseAmount: total,
        freightActualCost: actualCost,
        selectedCarrierName: action.data.selectedCarrierName || null,
      });
      setSelected(updated);
      setAction({ kind: null, data: {} });
      await load();
    } catch (err: any) { alert(err.message); }
  };

  // Box dimensions editor state
  const [boxes, setBoxes] = useState<Array<{ length: string; width: string; height: string; weight: string }>>([]);
  const [boxesSaving, setBoxesSaving] = useState(false);

  useEffect(() => {
    if (selected?.boxes) {
      setBoxes(selected.boxes.map((b: any) => ({
        length: b.length ?? '', width: b.width ?? '', height: b.height ?? '', weight: b.weight ?? '',
      })));
    } else {
      setBoxes([]);
    }
  }, [selected?.id]);

  const saveBoxes = async () => {
    if (!selected) return;
    setBoxesSaving(true);
    try {
      const payload = boxes
        .filter(b => b.length || b.width || b.height || b.weight)
        .map(b => ({
          length: parseFloat(b.length) || 0,
          width: parseFloat(b.width) || 0,
          height: parseFloat(b.height) || 0,
          weight: parseFloat(b.weight) || 0,
        }));
      await api.consolidations.setBoxes(selected.id, payload);
      const detail = await api.consolidations.get(selected.id);
      setSelected(detail);
    } catch (err: any) { alert(err.message); }
    finally { setBoxesSaving(false); }
  };

  const submitBook = async () => {
    if (!selected) return;
    try {
      const updated = await api.consolidations.book(selected.id, {
        bolNumber: action.data.bolNumber || undefined,
        proNumber: action.data.proNumber || undefined,
        pickupConfirmation: action.data.pickupConfirmation || undefined,
      });
      setSelected(updated);
      setAction({ kind: null, data: {} });
      await load();
    } catch (err: any) { alert(err.message); }
  };

  const submitShip = async () => {
    if (!selected) return;
    try {
      const updated = await api.consolidations.markShipped(selected.id, {
        trackingNumber: action.data.trackingNumber || undefined,
      });
      setSelected(updated);
      setAction({ kind: null, data: {} });
      await load();
    } catch (err: any) { alert(err.message); }
  };

  const submitEdit = async () => {
    if (!selected) return;
    try {
      const updated = await api.consolidations.updateShippingDetails(selected.id, {
        bolNumber: action.data.bolNumber ?? '',
        proNumber: action.data.proNumber ?? '',
        pickupConfirmation: action.data.pickupConfirmation ?? '',
        trackingNumber: action.data.trackingNumber ?? '',
      });
      setSelected(updated);
      setAction({ kind: null, data: {} });
      await load();
    } catch (err: any) { alert(err.message); }
  };

  const cancelShipment = async () => {
    if (!selected) return;
    if (!confirm('Cancel this consolidated shipment? All orders go back to standalone freight.')) return;
    try {
      await api.consolidations.cancel(selected.id);
      setSelected(null);
      await load();
    } catch (err: any) { alert(err.message); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Consolidated Shipments</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm border rounded px-3 py-1.5 bg-white"
        >
          <option value="active">Active (not delivered/cancelled)</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">{error}</div>}

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : shipments.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center text-gray-500">
          No consolidated shipments {filter !== 'active' ? `with status "${filter}"` : 'are active right now'}.
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2">Dealer</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Orders</th>
                <th className="text-right px-4 py-2">Items</th>
                <th className="text-right px-4 py-2">Freight</th>
                <th className="text-left px-4 py-2">Carrier</th>
                <th className="text-left px-4 py-2">BOL / Tracking</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shipments.map(s => (
                <tr key={s.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">{s.dealerCompany}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${STATUS_COLORS[s.status] || ''}`}>
                      {s.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{s.orderCount}</td>
                  <td className="px-4 py-2 text-right">{s.totalItemCount}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(s.freightTotalAmount)}</td>
                  <td className="px-4 py-2">{s.selectedCarrierName || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {s.trackingNumber || s.bolNumber || '—'}
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => openDetail(s.id)} className="text-xs text-blue-600 hover:underline">Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{selected.dealerCompany}</h2>
                  <p className="text-sm text-gray-600">
                    Shipment ID: {selected.id.slice(0, 8)}…
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded border font-medium ${STATUS_COLORS[selected.status] || ''}`}>
                      {selected.status.toUpperCase()}
                    </span>
                  </p>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                <div><span className="text-gray-500">Orders:</span> <strong>{selected.orders?.length ?? 0}</strong></div>
                <div><span className="text-gray-500">Total items:</span> <strong>{(selected.orders || []).reduce((s: number, o: any) => s + (parseInt(o.item_count, 10) || 0), 0)}</strong></div>
                <div><span className="text-gray-500">Freight (charged):</span> <strong>{fmt(selected.freightTotalAmount)}</strong></div>
                <div><span className="text-gray-500">Our carrier cost:</span> <strong>{selected.freightActualCost != null ? fmt(selected.freightActualCost) : '—'}</strong></div>
                {selected.freightTotalAmount != null && selected.freightActualCost != null && (
                  <div><span className="text-gray-500">Freight margin:</span> <strong className={(selected.freightTotalAmount - selected.freightActualCost) >= 0 ? 'text-green-700' : 'text-red-600'}>{fmt(selected.freightTotalAmount - selected.freightActualCost)}</strong></div>
                )}
                <div><span className="text-gray-500">Carrier:</span> <strong>{selected.selectedCarrierName || '—'}</strong></div>
                <div><span className="text-gray-500">BOL #:</span> <strong>{selected.bolNumber || '—'}</strong></div>
                <div><span className="text-gray-500">PRO #:</span> <strong>{selected.proNumber || '—'}</strong></div>
              </div>

              <div className="bg-gray-50 border rounded p-3 mb-4">
                <div className="text-xs font-semibold text-gray-700 mb-2">Orders in this shipment</div>
                {(selected.orders || []).map((o: any) => (
                  <div key={o.id} className="flex justify-between text-xs py-1">
                    <span>{o.order_number} {o.sidemark ? `— ${o.sidemark}` : ''} ({o.item_count} items, {o.status})</span>
                    <span className="font-mono">{fmt(o.freight_share)}</span>
                  </div>
                ))}
              </div>

              {/* Box dimensions — entered by shipping so freight quotes can be run without asking the office */}
              <div className="bg-gray-50 border rounded p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-700">Box Dimensions</div>
                  {(() => {
                    const tw = boxes.reduce((s, b) => s + (parseFloat(b.weight) || 0), 0);
                    return <div className="text-[11px] text-gray-500">{boxes.length} box{boxes.length === 1 ? '' : 'es'} · {tw.toFixed(0)} lbs total</div>;
                  })()}
                </div>
                {boxes.length === 0 && (
                  <p className="text-[11px] text-gray-400 mb-2">No boxes entered — add each box's dimensions so a freight quote can be run.</p>
                )}
                {boxes.map((b, i) => (
                  <div key={i} className="flex gap-1 mb-1 items-center">
                    {(['length', 'width', 'height', 'weight'] as const).map(f => (
                      <input
                        key={f}
                        type="number" step="0.1" placeholder={f === 'weight' ? 'lbs' : f[0].toUpperCase()}
                        value={b[f]}
                        onChange={e => setBoxes(boxes.map((x, xi) => xi === i ? { ...x, [f]: e.target.value } : x))}
                        className="w-16 px-1.5 py-1 border rounded text-xs"
                        title={f === 'weight' ? 'Weight (lbs)' : `${f} (in)`}
                      />
                    ))}
                    <span className="text-[10px] text-gray-400">L×W×H in · wt lbs</span>
                    <button
                      onClick={() => setBoxes(boxes.filter((_, xi) => xi !== i))}
                      className="ml-auto text-red-400 hover:text-red-600 text-xs px-1"
                    >✕</button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setBoxes([...boxes, { length: '', width: '', height: '', weight: '' }])}
                    className="px-2 py-1 text-[11px] border border-gray-300 rounded hover:bg-gray-100"
                  >+ Add box</button>
                  <button
                    onClick={saveBoxes}
                    disabled={boxesSaving}
                    className="px-2 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >{boxesSaving ? 'Saving…' : 'Save boxes'}</button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {['building', 'quoted'].includes(selected.status) && (
                  <button
                    onClick={() => setAction({ kind: 'quote', data: { freightTotalAmount: selected.freightTotalAmount || '', freightActualCost: selected.freightActualCost ?? '', selectedCarrierName: selected.selectedCarrierName || '' } })}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Set freight quote
                  </button>
                )}
                {selected.status === 'quoted' && (
                  <button
                    onClick={() => setAction({ kind: 'book', data: { bolNumber: '', proNumber: '', pickupConfirmation: '' } })}
                    className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Book with carrier
                  </button>
                )}
                {selected.status === 'booked' && (
                  <button
                    onClick={() => setAction({ kind: 'ship', data: { trackingNumber: '' } })}
                    className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Mark shipped
                  </button>
                )}
                {/* Always-available edit for BOL/PRO/pickup/tracking — typo fixes */}
                <button
                  onClick={() => setAction({ kind: 'edit', data: {
                    bolNumber: selected.bolNumber || '',
                    proNumber: selected.proNumber || '',
                    pickupConfirmation: selected.pickupConfirmation || '',
                    trackingNumber: selected.trackingNumber || '',
                  } })}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Edit shipping details
                </button>
                {!['shipped', 'delivered', 'cancelled'].includes(selected.status) && (
                  <button
                    onClick={cancelShipment}
                    className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50"
                  >
                    Cancel shipment
                  </button>
                )}
              </div>

              {action.kind === 'quote' && (
                <div className="mt-4 p-3 border rounded bg-blue-50">
                  <div className="text-xs font-semibold mb-2">Set freight quote</div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Freight charged to dealer ($)</label>
                  <input
                    type="number" step="0.01" placeholder="Freight total ($)"
                    value={action.data.freightTotalAmount}
                    onChange={e => setAction({ ...action, data: { ...action.data, freightTotalAmount: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm"
                  />
                  <label className="block text-[11px] text-gray-500 mb-0.5">Our carrier cost ($) — flows to factory COGS</label>
                  <input
                    type="number" step="0.01" placeholder="What we pay the carrier ($)"
                    value={action.data.freightActualCost}
                    onChange={e => setAction({ ...action, data: { ...action.data, freightActualCost: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm"
                  />
                  <input
                    type="text" placeholder="Carrier name (optional)"
                    value={action.data.selectedCarrierName}
                    onChange={e => setAction({ ...action, data: { ...action.data, selectedCarrierName: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm"
                  />
                  <div className="flex gap-2">
                    <button onClick={submitQuote} className="px-3 py-1 text-xs bg-blue-600 text-white rounded">Save quote</button>
                    <button onClick={() => setAction({ kind: null, data: {} })} className="px-3 py-1 text-xs bg-white border rounded">Cancel</button>
                  </div>
                </div>
              )}

              {action.kind === 'book' && (
                <div className="mt-4 p-3 border rounded bg-purple-50">
                  <div className="text-xs font-semibold mb-2">Book with carrier</div>
                  <input type="text" placeholder="BOL #" value={action.data.bolNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, bolNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <input type="text" placeholder="PRO #" value={action.data.proNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, proNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <input type="text" placeholder="Pickup confirmation" value={action.data.pickupConfirmation}
                    onChange={e => setAction({ ...action, data: { ...action.data, pickupConfirmation: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <div className="flex gap-2">
                    <button onClick={submitBook} className="px-3 py-1 text-xs bg-purple-600 text-white rounded">Confirm booking</button>
                    <button onClick={() => setAction({ kind: null, data: {} })} className="px-3 py-1 text-xs bg-white border rounded">Cancel</button>
                  </div>
                </div>
              )}

              {action.kind === 'ship' && (
                <div className="mt-4 p-3 border rounded bg-green-50">
                  <div className="text-xs font-semibold mb-2">Mark shipped (will mark all orders shipped too)</div>
                  <input type="text" placeholder="Tracking #" value={action.data.trackingNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, trackingNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <div className="flex gap-2">
                    <button onClick={submitShip} className="px-3 py-1 text-xs bg-green-600 text-white rounded">Mark shipped</button>
                    <button onClick={() => setAction({ kind: null, data: {} })} className="px-3 py-1 text-xs bg-white border rounded">Cancel</button>
                  </div>
                </div>
              )}

              {action.kind === 'edit' && (
                <div className="mt-4 p-3 border rounded bg-gray-50">
                  <div className="text-xs font-semibold mb-2">Edit shipping details</div>
                  <div className="text-[11px] text-gray-500 mb-2">
                    Fix typos in BOL / PRO / pickup / tracking. Doesn't change shipment status. Leave a field blank to clear it.
                  </div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">BOL #</label>
                  <input type="text" placeholder="BOL #" value={action.data.bolNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, bolNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <label className="block text-[11px] text-gray-500 mb-0.5">PRO #</label>
                  <input type="text" placeholder="PRO #" value={action.data.proNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, proNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <label className="block text-[11px] text-gray-500 mb-0.5">Pickup confirmation</label>
                  <input type="text" placeholder="Pickup confirmation" value={action.data.pickupConfirmation}
                    onChange={e => setAction({ ...action, data: { ...action.data, pickupConfirmation: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <label className="block text-[11px] text-gray-500 mb-0.5">Tracking #</label>
                  <input type="text" placeholder="Tracking #" value={action.data.trackingNumber}
                    onChange={e => setAction({ ...action, data: { ...action.data, trackingNumber: e.target.value } })}
                    className="w-full mb-2 px-2 py-1 border rounded text-sm" />
                  <div className="flex gap-2">
                    <button onClick={submitEdit} className="px-3 py-1 text-xs bg-blue-600 text-white rounded">Save changes</button>
                    <button onClick={() => setAction({ kind: null, data: {} })} className="px-3 py-1 text-xs bg-white border rounded">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
